// Notes de frais, cagnotte du titulaire et charges fixes — rendu.
// Les règles comptables vivent dans lib/frais-logic.js, où elles sont testées.
//
// Entrée principale voulue par Mehdi : « ajouter une note de frais par une
// simple photo ». Sur mobile, le bouton ouvre directement l'appareil photo
// (capture="environment") ; le formulaire se remplit ensuite, pas l'inverse.

import {
  completerMontants,
  validerNote,
  soldeCagnotte,
  validerRemboursement,
  coutMensuelEquivalent,
} from "../lib/frais-logic.js";
import { eur2, fdate } from "../lib/format.js";

const el = (tag, cls, texte) => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (texte != null) n.textContent = texte;
  return n;
};

const champ = (label, input) => {
  const w = el("div", "champ");
  w.appendChild(el("label", null, label));
  w.appendChild(input);
  return w;
};

const input = (type, attrs = {}) => {
  const n = document.createElement("input");
  n.type = type;
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
  return n;
};

const MODES_LABEL = {
  virement: "Virement",
  especes_caisse: "Espèces (caisse)",
  cheque: "Chèque",
  compensation: "Compensation",
  autre: "Autre",
};

const CATEGORIES_CHARGE = {
  loyer: "Loyer", energie: "Énergie", assurance: "Assurance", impot: "Impôts et taxes",
  abonnement: "Abonnement", credit: "Crédit", personnel: "Personnel",
  telecom: "Télécom", maintenance: "Maintenance", autre: "Autre",
};

// ── Notes de frais ───────────────────────────────────────────────────────────

function rendreFormulaireNote(api, apresEnregistrement) {
  const form = el("form", "form-frais");
  const erreurs = el("div", "form-err");

  const photo = input("file", { accept: "image/*", capture: "environment" });
  photo.className = "photo-input";

  const dateI = input("date", { required: "required" });
  dateI.value = new Date().toISOString().slice(0, 10);
  const libelleI = input("text", { placeholder: "Ex. : fournitures de bureau", required: "required" });
  const fournisseurI = input("text", { placeholder: "Ex. : Monoprix" });
  const ttcI = input("number", { step: "0.01", min: "0", placeholder: "0,00" });
  const tvaI = input("number", { step: "0.01", min: "0", placeholder: "TVA (facultatif)" });

  const nature = document.createElement("select");
  for (const [v, t] of [
    ["pro", "Dépense de l'officine (payée par l'officine)"],
    ["perso", "J'ai avancé de ma poche (alimente ma cagnotte)"],
  ]) {
    const o = document.createElement("option");
    o.value = v; o.textContent = t;
    nature.appendChild(o);
  }

  // Aide au remplissage : dès que TTC et TVA sont connus, le HT s'affiche. On ne
  // demande jamais les trois — c'est la meilleure façon d'obtenir un triplet
  // incohérent.
  const apercu = el("div", "muted");
  const majApercu = () => {
    const m = completerMontants({ ttc: ttcI.value, tva: tvaI.value });
    apercu.textContent = m.ht != null ? `Soit ${eur2(m.ht)} HT` : "";
  };
  ttcI.addEventListener("input", majApercu);
  tvaI.addEventListener("input", majApercu);

  form.append(
    champ("Photo du justificatif", photo),
    champ("Date de la dépense", dateI),
    champ("Libellé", libelleI),
    champ("Commerçant / fournisseur", fournisseurI),
    champ("Montant TTC payé", ttcI),
    champ("Dont TVA", tvaI),
    champ("Nature", nature),
    apercu,
    erreurs,
  );

  const submit = el("button", "btn btn-primary", "Enregistrer la note");
  submit.type = "submit";
  form.appendChild(submit);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    erreurs.textContent = "";
    const montants = completerMontants({ ttc: ttcI.value, tva: tvaI.value });
    const note = {
      date_depense: dateI.value,
      libelle: libelleI.value,
      fournisseur: fournisseurI.value || null,
      montant_ttc: montants.ttc,
      montant_tva: montants.tva,
      montant_ht: montants.ht,
      nature: nature.value,
      statut: "brouillon",
    };
    const pbs = validerNote(note);
    if (pbs.length) { erreurs.textContent = pbs.join(" "); return; }

    submit.disabled = true;
    submit.textContent = "Enregistrement…";
    try {
      const { note: creee } = await api("frais_upsert", note);

      // La photo part APRÈS la création : elle a besoin de l'identifiant de la
      // note pour son chemin, et une note sans photo reste un brouillon
      // récupérable — alors qu'une photo orpheline ne sert à rien.
      if (photo.files && photo.files[0]) {
        const f = photo.files[0];
        const ext = (f.name.split(".").pop() || "jpg").toLowerCase();
        const { url, token } = await api("frais_upload_url", { note_id: creee.id, extension: ext });
        if (url && token) {
          const r = await fetch(url, {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": f.type || "image/jpeg" },
            body: f,
          });
          if (!r.ok) throw new Error("le justificatif n'a pas pu être envoyé");
        }
      }
      form.reset();
      dateI.value = new Date().toISOString().slice(0, 10);
      apercu.textContent = "";
      apresEnregistrement();
    } catch (err) {
      erreurs.textContent = "Échec : " + err.message;
    } finally {
      submit.disabled = false;
      submit.textContent = "Enregistrer la note";
    }
  });

  return form;
}

function rendreListeNotes(donnees, api, recharger) {
  const box = el("div");
  const rows = donnees?.rows ?? [];
  if (!rows.length) {
    box.appendChild(el("div", "muted", "Aucune note de frais enregistrée."));
    return box;
  }
  for (const n of rows) {
    const ligne = el("div", `note note-${n.statut}`);
    ligne.appendChild(el("span", "note-date", fdate(n.date_depense)));
    ligne.appendChild(el("span", "note-lib", n.libelle));
    ligne.appendChild(el("span", "note-nature", n.nature === "perso" ? "avance perso" : "officine"));
    ligne.appendChild(el("span", "note-montant", eur2(n.montant_ttc)));

    if (n.statut === "brouillon") {
      const b = el("button", "btn btn-mini", "Valider");
      b.type = "button";
      // Le message d'aide est là AVANT le clic : sans justificatif, la
      // validation échouera côté serveur, autant le dire tout de suite.
      if (!n.a_justificatif) {
        b.disabled = true;
        b.title = "Ajoutez d'abord le justificatif : sans pièce, la dépense n'est pas déductible.";
      }
      b.addEventListener("click", async () => {
        b.disabled = true;
        try { await api("frais_valider", { id: n.id }); recharger(); }
        catch (e) { b.disabled = false; alertToast(e.message); }
      });
      ligne.appendChild(b);
    } else {
      ligne.appendChild(el("span", "note-statut", n.statut === "validee" ? "validée" : "annulée"));
    }
    box.appendChild(ligne);
  }
  return box;
}

function alertToast(msg) {
  if (typeof window.toast === "function") window.toast(msg, "err");
}

// ── Cagnotte ─────────────────────────────────────────────────────────────────

function rendreCagnotte(donnees, api, recharger) {
  const box = el("div");
  const mvts = donnees?.mouvements ?? [];
  const s = soldeCagnotte(mvts);

  const carte = el("div", "synth-bloc");
  carte.appendChild(el("div", "tuile-titre", "L'officine vous doit"));
  carte.appendChild(el("div", "tuile-valeur", eur2(s.solde)));
  carte.appendChild(el("div", "tuile-detail",
    `${eur2(s.avances)} avancés · ${eur2(s.rembourse)} déjà récupérés · ${s.n} mouvements`));
  box.appendChild(carte);

  // Solder — un événement daté, avec son mode de récupération obligatoire.
  const form = el("form", "form-frais");
  const montantI = input("number", { step: "0.01", min: "0", placeholder: "0,00" });
  montantI.value = s.solde > 0 ? String(s.solde) : "";
  const mode = document.createElement("select");
  const vide = document.createElement("option");
  vide.value = ""; vide.textContent = "— comment avez-vous récupéré l'argent ? —";
  mode.appendChild(vide);
  for (const [v, t] of Object.entries(MODES_LABEL)) {
    const o = document.createElement("option");
    o.value = v; o.textContent = t;
    mode.appendChild(o);
  }
  const refI = input("text", { placeholder: "Référence (n° de virement, de chèque…)" });
  const dateI = input("date");
  dateI.value = new Date().toISOString().slice(0, 10);
  const err = el("div", "form-err");

  form.append(
    champ("Montant récupéré", montantI),
    champ("Mode de récupération", mode),
    champ("Référence", refI),
    champ("Date", dateI),
    err,
  );
  const b = el("button", "btn btn-primary", "Solder");
  b.type = "submit";
  form.appendChild(b);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    err.textContent = "";
    const pbs = validerRemboursement(
      { montant_ttc: montantI.value, mode_recuperation: mode.value }, s.solde);
    // Un dépassement de créance est un AVERTISSEMENT, pas un refus : le
    // titulaire a le droit, ça change juste de nature comptable.
    const bloquants = pbs.filter((p) => !/plus que/i.test(p));
    err.textContent = pbs.join(" ");
    if (bloquants.length) return;

    b.disabled = true;
    try {
      await api("cagnotte_solder", {
        montant_ttc: Number(montantI.value),
        mode_recuperation: mode.value,
        reference: refI.value || null,
        date_mouvement: dateI.value,
      });
      recharger();
    } catch (e2) { err.textContent = "Échec : " + e2.message; }
    finally { b.disabled = false; }
  });

  box.appendChild(el("h2", "sect", "Solder la cagnotte"));
  box.appendChild(form);

  box.appendChild(el("h2", "sect", "Mouvements"));
  if (!mvts.length) box.appendChild(el("div", "muted", "Aucun mouvement."));
  for (const m of mvts) {
    const l = el("div", "note");
    l.appendChild(el("span", "note-date", fdate(m.date_mouvement)));
    l.appendChild(el("span", "note-lib",
      m.sens === "avance" ? "Avance" : m.sens === "remboursement" ? "Remboursement" : "Ajustement"));
    if (m.mode_recuperation) {
      l.appendChild(el("span", "note-nature", MODES_LABEL[m.mode_recuperation] ?? m.mode_recuperation));
    }
    l.appendChild(el("span", "note-montant", eur2(m.montant_ttc)));
    box.appendChild(l);
  }
  return box;
}

// ── Charges fixes ────────────────────────────────────────────────────────────

function rendreCharges(donnees) {
  const box = el("div");
  const rows = donnees?.rows ?? [];

  const carte = el("div", "synth-bloc");
  carte.appendChild(el("div", "tuile-titre", "Charges fixes — équivalent mensuel"));
  carte.appendChild(el("div", "tuile-valeur", eur2(donnees?.total_mensuel_equivalent)));
  // Toutes périodicités ramenées au mois : c'est le seul chiffre qui permette de
  // comparer un loyer mensuel et une assurance annuelle.
  carte.appendChild(el("div", "tuile-detail",
    "toutes périodicités ramenées au mois, charges actives seulement"));
  box.appendChild(carte);

  if (!rows.length) {
    box.appendChild(el("div", "muted", "Aucune charge fixe enregistrée."));
    return box;
  }
  for (const c of rows) {
    const l = el("div", `note${c.actif ? "" : " note-annulee"}`);
    l.appendChild(el("span", "note-lib", c.libelle));
    l.appendChild(el("span", "note-nature", CATEGORIES_CHARGE[c.categorie] ?? c.categorie));
    l.appendChild(el("span", "note-date", c.periodicite));
    l.appendChild(el("span", "note-montant", eur2(c.montant_ttc)));
    const eq = coutMensuelEquivalent(c);
    if (eq != null && c.periodicite !== "mensuelle") {
      l.appendChild(el("span", "muted", `≈ ${eur2(eq)}/mois`));
    }
    box.appendChild(l);
  }
  return box;
}

// ── Assemblage ───────────────────────────────────────────────────────────────

export async function chargerFrais(api) {
  const [notes, cagnotte, charges] = await Promise.all([
    api("frais_list", {}).catch(() => null),
    api("cagnotte_solde", {}).catch(() => null),
    api("charges_list", {}).catch(() => null),
  ]);
  return { notes, cagnotte, charges };
}

export function rendreFrais(racine, donnees, api, recharger) {
  const frag = document.createDocumentFragment();

  frag.appendChild(el("h2", "sect", "Nouvelle note de frais"));
  frag.appendChild(rendreFormulaireNote(api, recharger));

  frag.appendChild(el("h2", "sect", "Mes notes de frais"));
  frag.appendChild(rendreListeNotes(donnees.notes, api, recharger));

  frag.appendChild(el("h2", "sect", "Ma cagnotte"));
  frag.appendChild(rendreCagnotte(donnees.cagnotte, api, recharger));

  frag.appendChild(el("h2", "sect", "Charges fixes"));
  frag.appendChild(rendreCharges(donnees.charges));

  racine.replaceChildren(frag);
}
